export type AudioLevel = {
  linear: number;
  dbfs: number;
  clipping: boolean;
  silent: boolean;
};

let sharedContext: AudioContext | undefined;

export function getAudioContext(): AudioContext {
  sharedContext ??= new AudioContext();
  if (sharedContext.state === "suspended") {
    void sharedContext.resume();
  }
  return sharedContext;
}

export class AudioLevelMonitor {
  private readonly context: AudioContext;
  private readonly analyser: AnalyserNode;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly samples: Float32Array<ArrayBuffer>;
  private animationFrame = 0;
  private silentSince = performance.now();
  private stopped = false;

  constructor(
    stream: MediaStream,
    private readonly onLevel: (level: AudioLevel) => void,
  ) {
    this.context = getAudioContext();
    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 1024;
    this.analyser.smoothingTimeConstant = 0.7;
    this.samples = new Float32Array(
      new ArrayBuffer(this.analyser.fftSize * Float32Array.BYTES_PER_ELEMENT),
    );
    this.source = this.context.createMediaStreamSource(stream);
    this.source.connect(this.analyser);
    this.measure = this.measure.bind(this);
    this.measure();
  }

  stop(): void {
    this.stopped = true;
    cancelAnimationFrame(this.animationFrame);
    this.source.disconnect();
    this.analyser.disconnect();
  }

  private measure(): void {
    if (this.stopped) return;
    this.analyser.getFloatTimeDomainData(this.samples);
    let sum = 0;
    let peak = 0;
    for (const sample of this.samples) {
      sum += sample * sample;
      peak = Math.max(peak, Math.abs(sample));
    }
    const rms = Math.sqrt(sum / this.samples.length);
    const dbfs = rms > 0.00001 ? 20 * Math.log10(rms) : -100;
    const linear = Math.min(1, Math.max(0, (dbfs + 60) / 60));
    if (dbfs > -50) {
      this.silentSince = performance.now();
    }
    this.onLevel({
      linear,
      dbfs,
      clipping: peak >= 0.98,
      silent: performance.now() - this.silentSince > 4_000,
    });
    this.animationFrame = requestAnimationFrame(this.measure);
  }
}

export async function playSpeakerTest(
  volume = 1,
  outputDeviceId = "",
): Promise<void> {
  const context = getAudioContext();
  const sinkContext = context as AudioContext & {
    setSinkId?: (sinkId: string) => Promise<void>;
  };
  if (outputDeviceId && sinkContext.setSinkId) {
    await sinkContext.setSinkId(outputDeviceId);
  }
  await context.resume();
  const now = context.currentTime;
  const gain = context.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(
    Math.max(0.0001, 0.22 * Math.min(1, Math.max(0, volume))),
    now + 0.03,
  );
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.58);
  gain.connect(context.destination);

  for (const [index, frequency] of [523.25, 659.25, 783.99].entries()) {
    const oscillator = context.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.value = frequency;
    oscillator.connect(gain);
    oscillator.start(now + index * 0.12);
    oscillator.stop(now + 0.62);
  }
}
